import React, { useState, useEffect, createContext, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useGlobalContext } from '../context/GlobalContext';
import { selfcareApi } from '../services/SelfcareApiService';
import { useAmountFormatter } from '../hooks/useAmountFormatter';
import ActivateModal from '../components/modals/ActivateModal';
import ConvertModal from '../components/modals/ConvertModal';
import DetailsModal from '../components/modals/DetailsModal';
import { EmailContent } from '../components/EmailContent';

const CardsContext = createContext();

export const CardsProvider = ({ children, token }) => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ready, setReady] = useState(false);
  const { siteInfo, setError: setGlobalError } = useGlobalContext();
  const navigate = useNavigate();

  const cardCodeTypes = {
    0: 'Business',
    1: 'Carte cadeau',
    2: 'Confirmation de commande',
    3: 'Remboursement',
    4: 'Bon d\'achat'
  };

  const loadCards = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await selfcareApi.getCards(token);
      
      if (response.status === 'ok' && response.result && response.result.length > 0) {
        const processedCards = response.result.map(card => {
          const processedCard = {
            ...card,
            activation_date: card.activation_date ? card.activation_date * 1000 : null,
            expiration_date: card.expiration_date ? card.expiration_date * 1000 : null,
            send_date: card.send_date ? card.send_date * 1000 : null,
          };

          const postActivationEnabled =
            (card.diffusion_post_selfcare_activation === null && siteInfo?.postActivation) ||
            card.diffusion_post_selfcare_activation === true;
          
          processedCard.can_activate =
            (card.is_physical && card.physical_status === 'sent' && !card.activation_date) ||
            (!card.is_physical && postActivationEnabled && !card.activation_date);

          processedCard.card_code_type_value =
            (card.card_code_type !== null && card.card_code_type in cardCodeTypes)
              ? cardCodeTypes[card.card_code_type]
              : 'Carte';
              
          processedCard.card_code_type_header =
            (card.card_code_type !== null && card.card_code_type in cardCodeTypes)
              ? cardCodeTypes[card.card_code_type]
              : 'Carte cadeau';

          return processedCard;
        });

        setCards(processedCards);
        setTimeout(() => setReady(true), 1000);
      } else {
        setGlobalError('no_card');
        navigate('/error?type=no_card');
      }
    } catch (err) {
      console.error('Error loading cards:', err);
      setGlobalError('network_error');
      navigate('/error');
    } finally {
      setLoading(false);
    }
  };

  const activateCard = async (cardId) => {
    try {
      const card = cards.find(c => c.id === cardId);
      if (!card) {
        throw new Error('Card not found');
      }

      const response = await selfcareApi.activatePhysicalCard(card);
      if (response.status === 'ok') {
        await loadCards();
        return true;
      } else {
        throw new Error(response.message || 'Activation failed');
      }
    } catch (err) {
      console.error('Error activating card:', err);
      setGlobalError('activation_error');
      navigate('/error');
      return false;
    }
  };

  const convertCard = async (cardId) => {
    try {
      const card = cards.find(c => c.id === cardId);
      if (!card) {
        throw new Error('Card not found');
      }

      const response = await selfcareApi.convertPhysicalCard(card);
      if (response.status === 'ok') {
        await loadCards();
        return true;
      } else {
        throw new Error(response.message || 'Conversion failed');
      }
    } catch (err) {
      console.error('Error converting card:', err);
      setGlobalError('conversion_error');
      navigate('/error');
      return false;
    }
  };

  useEffect(() => {
    if (token) {
      loadCards();
    }
  }, [token]);

  const value = {
    cards,
    loading,
    error,
    ready,
    loadCards,
    activateCard,
    convertCard
  };

  return (
    <CardsContext.Provider value={value}>
      {children}
    </CardsContext.Provider>
  );
};

export const useCards = () => {
  const context = useContext(CardsContext);
  if (!context) {
    throw new Error('useCards must be used within a CardsProvider');
  }
  return context;
};



const CardsApp = () => {
  const navigate = useNavigate();
  
  const getTokenFromUrl = () => {
    const path = window.location.pathname;
    const parts = path.split('/cards/');
    return parts[1] || null;
  };

  const token = getTokenFromUrl();

  useEffect(() => {
    if (!token) {
      navigate('/error?type=no_token');
    }
  }, [token, navigate]);



  return (
    <CardsProvider token={token}>
      <CardsContent token={token} />
    </CardsProvider>
  );
};

const CardsContent = ({ token }) => {
  const { cards, loading } = useCards(); 
  const { siteInfo,currentLang } = useGlobalContext();
  const { t } = useTranslation();
  const formatAmount = useAmountFormatter();
  const [selectedCard, setSelectedCard] = useState(null);

  const [showActivateModal, setShowActivateModal] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [modalCard, setModalCard] = useState(null);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        {t('Chargement...')}
      </div>
    );
  }

  if (!cards || cards.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <h2>{t('Aucune carte trouvée')}</h2>
        <p>{t('Vous n\'avez actuellement aucun bon d\'achat.')}</p>
      </div>
    );
  }

  const openDetailsModal = (card) => {
    setModalCard(card);
    setShowDetailsModal(true);
  };

  const openConfirmActivateModal = (card) => {
    setModalCard(card);
    setShowActivateModal(true);
  };

  const openConfirmConvertModal = (card) => {
    setModalCard(card);
    setShowConvertModal(true);
  };

  const showEmail = (card) => {
    setSelectedCard(card);
  };

  const showList = () => {
    setSelectedCard(null);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    return new Date(timestamp).toLocaleDateString('fr-FR');
  };

  const getCardTypeDisplay = (card, isPhysical) => {
    const baseType = t(card.card_code_type_value);
    if (isPhysical) {
      return baseType+' physique';
    } else {
      return baseType+' digitale';
    }
  };

  return (
    <>
      {!selectedCard ? (
        <div id="cards">
          <h1 className="text-center">
            {t('Vos bons d\'achat {{ siteInfo.libelle }}', { 
              'siteInfo.libelle': siteInfo?.libelle || '' 
            })}
          </h1>

          <section className="user-infos">
            <h2 className="text-center">{t('Vos informations')}</h2>
            <div>
              <div>
                <span>{t('Prénom')}</span><br/>{cards[0].firstname}
              </div>
              <div>
                <span>{t('Nom')}</span><br/>{cards[0].lastname}
              </div>
            </div>
            <div>
              <div>
                <span>{t('Adresse email')}</span><br/>{cards[0].email}
              </div>
            </div>
          </section>

          <section className="list">
            <h2 className="text-center">{t('Vos bons d\'achat')}</h2>
            <div>
              {cards.map(card => (
                <article key={card.id} className={card.is_expired ? 'expired' : ''}>
                  <header className="info">
                    <div>
                      <span>{t(card.card_code_type_header)}</span><br/>{card.code}
                    </div>
                    {card.have_linked_cards && (
                      <div>
                        <i className="ic-multiple_card"></i>
                      </div>
                    )}
                  </header>

                  <div className="dates">
                    {card.activation_date ? (
                      <div>
                        <span>{t('Date d\'activation')}</span><br/>
                        {formatDate(card.activation_date)}
                      </div>
                    ) : (
                      <div>
                        <span>{t('Date d\'activation')}</span><br/>
                        {t('En cours')}
                      </div>
                    )}
                    <div>
                      <span>{t('Date d\'expiration')}</span><br/>
                      {formatDate(card.expiration_date)}
                    </div>
                  </div>

                  <div className="amount">
                    <div>
                      <span>{t('Type')}</span><br/>
                      {getCardTypeDisplay(card, card.is_physical)}
                    </div>
                    <div>
                      <span>{t('Valeur')}</span><br/>
                      {formatAmount(card.amount,siteInfo,currentLang)}
                    </div>
                  </div>

                  <div className="links">
                    {card.is_physical && (
                      <a
                        href={`cards/${token}`}
                        className="dateMode active"
                        onClick={(e) => { e.preventDefault(); openDetailsModal(card); }}
                      >
                        {t('Détails')}
                      </a>
                    )}

                    {(!card.is_physical || card.physical_status === 'converted') && (
                      <a
                        href={`cards/${token}`}
                        className="dateMode active"
                        onClick={(e) => { e.preventDefault(); showEmail(card); }}
                      >
                        {t('Voir l\'email envoyé')}
                      </a>
                    )}

                    {!card.is_expired && (!card.is_physical || card.physical_status === 'converted') && (
                      <>
                        <a href={card.download_link} className="dateMode active">
                          {t('Télécharger sur mon mobile')}
                        </a>
                        <a href={card.print_link} className="dateMode active">
                          {t('Imprimer')}
                        </a>
                      </>
                    )}

                    {card.is_physical && card.physical_status === 'sent' && (
                      <a 
                        href={card.physical_tracking_url} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="dateMode active"
                      >
                        {t('Suivre la commande')}
                      </a>
                    )}

                    {card.can_activate && (
                      <a
                        href={`cards/${token}`}
                        onClick={(e) => { e.preventDefault(); openConfirmActivateModal(card); }}
                        className="dateMode active"
                      >
                        {t('Activer la carte')}
                      </a>
                    )}

                    {card.is_physical && (card.physical_status === 'sent' || card.physical_status === 'received') && (
                      <a
                        href={`cards/${token}`}
                        onClick={(e) => { e.preventDefault(); openConfirmConvertModal(card); }}
                        className="dateMode active"
                      >
                        {t('Transformer ma carte')}
                      </a>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </section>
        </div>
      ) : (
        <div id="emailContent">
          <h3 className="text-center">
            {t('Bons d\'achat n° {{ card.code }}', { 'card.code': selectedCard.code })}
          </h3>
          
          <h4 className="text-center">{t('Email envoyé')}</h4>
          
          <p className="text-center">
            {t('Retrouvez ci-dessous la copie de l\'email qui vous a été envoyé le {{ card.send_date | date: \'dd/MM/yyyy\' }} à l\'adresse {{ card.email }}', {
              'card.send_date | date: \'dd/MM/yyyy\'': formatDate(selectedCard.send_date),
              'card.email': selectedCard.email
            })}
          </p>

          <EmailContent content={selectedCard.email_content} />
          
          <div className="text-center">
            <a
              href="#"
              className="back dateMode active"
              onClick={(e) => { e.preventDefault(); showList(); }}
            >
              {t('Retourner à la liste de vos bons d\'achat')}
            </a>
          </div>
        </div>
      )}

       <ActivateModal isOpen={showActivateModal} onClose={() => setShowActivateModal(false)} card={modalCard} />
      <ConvertModal isOpen={showConvertModal} onClose={() => setShowConvertModal(false)} card={modalCard} />
      <DetailsModal isOpen={showDetailsModal} onClose={() => setShowDetailsModal(false)} card={modalCard} />  

    </>
  );
};

export default CardsApp;